import { useEffect, useRef, useState } from 'react';
import {
  useListBulkReprocessRunItems,
  BulkReprocessRunItemDtoStatus,
  type ListBulkReprocessRunItemsStatus,
} from '@sara/api-client';
import { Badge } from '@sara/ui/components/badge';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';
import { Skeleton } from '@sara/ui/components/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sara/ui/components/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sara/ui/components/table';
import { getApiErrorMessage } from '../../../../lib/api-error';
import { ITEM_STATUS_VARIANT } from './display';

const PAGE_SIZE = 25;
const ALL = 'ALL';

interface BulkReprocessItemsTableProps {
  runId: string;
  /** Poll while the run is still active so item statuses update live. */
  isRunActive: boolean;
}

export function BulkReprocessItemsTable({ runId, isRunActive }: BulkReprocessItemsTableProps) {
  const [status, setStatus] = useState<ListBulkReprocessRunItemsStatus | typeof ALL>(ALL);
  const [page, setPage] = useState(1);

  const { data, isLoading, error, isFetching, refetch } = useListBulkReprocessRunItems(
    runId,
    { status: status === ALL ? undefined : status, page, pageSize: PAGE_SIZE },
    { query: { refetchInterval: isRunActive ? 4000 : false } }
  );

  // Polling stops the instant the run flips to a terminal state, but the last item
  // writes may land a moment later — do one final refetch on that transition so
  // lingering RUNNING rows are replaced without waiting for a filter/page change.
  const wasRunActive = useRef(isRunActive);
  useEffect(() => {
    if (wasRunActive.current && !isRunActive) void refetch();
    wasRunActive.current = isRunActive;
  }, [isRunActive, refetch]);

  function selectStatus(next: string) {
    setStatus(next as ListBulkReprocessRunItemsStatus | typeof ALL);
    setPage(1);
  }

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">Sessions in this run</h3>
        <Select value={status} onValueChange={selectStatus}>
          <SelectTrigger className="h-9 w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {Object.values(BulkReprocessRunItemDtoStatus).map((s) => (
              <SelectItem key={s} value={s}>
                {s.charAt(0) + s.slice(1).toLowerCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid gap-2" data-testid="bulk-reprocess-items-loading">
          <Skeleton className="h-10 rounded-lg" />
          <Skeleton className="h-10 rounded-lg" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {getApiErrorMessage(error) ?? 'Failed to load items'}
        </div>
      ) : (
        <DataPanel>
          <DataPanel.Body className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Session ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Last error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items ?? []).map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {item.interviewSessionId.slice(0, 8)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={ITEM_STATUS_VARIANT[item.status]}>{item.status}</Badge>
                    </TableCell>
                    <TableCell className="tabular-nums">{item.attempts}</TableCell>
                    <TableCell className="max-w-[360px] truncate font-mono text-xs text-destructive">
                      {item.lastError ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DataPanel.Body>
          <DataPanel.Footer className="flex items-center justify-between">
            <span className="tabular-nums text-muted-foreground">
              {total === 0 ? 'No matching sessions' : `Showing ${from}–${to} of ${total}`}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </Button>
              <span className="tabular-nums text-[12.5px] text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </DataPanel.Footer>
        </DataPanel>
      )}
    </section>
  );
}
